import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createTransform,
  persistReducer,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import dayjs from "dayjs";

import authSlice from "./slices/authSlice";
import cartSlice from "./slices/cartSlice";
import contentSlice from "./slices/contentSlice";
import filterProductSlice from "./slices/filterProductSlice";

// children are reloaded from outlet-parents/me/ — not the source of truth when persisted
const authPersistConfig = {
  key: "auth",
  version: 2,
  storage,
  blacklist: [
    "children",
    "childrenLoaded",
    "selectedCustomer",
    "switchingLocked",
    "pendingSwitchCustomerId",
  ],
  /**
   * v2: drop in-memory family fields; keep token/user/selectedCustomerId.
   * me/ reloads children and clears selectedCustomerId if the child was unlinked.
   * Avoids crashes from old sessions that only had user.customer.
   */
  migrate: (state) => {
    if (!state) return Promise.resolve(undefined);
    return Promise.resolve({
      ...state,
      children: [],
      childrenLoaded: false,
      selectedCustomer: null,
      switchingLocked: false,
      pendingSwitchCustomerId: null,
      selectedOutlet: state.selectedOutlet || false,
    });
  },
};

/** Persist unpaid cart + meal date (PA2-04). Dayjs ↔ ISO string per key. */
const cartDateTransform = createTransform(
  (inboundState, key) => {
    if (key === "orderDate") {
      return inboundState ? dayjs(inboundState).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
    }
    return inboundState;
  },
  (outboundState, key) => {
    if (key === "orderDate") {
      return outboundState
        ? dayjs(outboundState).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");
    }
    if (key === "items" && !Array.isArray(outboundState)) {
      return [];
    }
    return outboundState;
  },
  { whitelist: ["orderDate", "items"] }
);

const cartPersistConfig = {
  key: "cart",
  version: 1,
  storage,
  whitelist: ["items", "orderDate", "customerId", "menuServingTimeId"],
  transforms: [cartDateTransform],
};

const persistConfig = {
  key: "root",
  version: 1,
  storage,
  blacklist: ["cart", "auth"],
};

const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authSlice.reducer),
  content: contentSlice.reducer,
  cart: persistReducer(cartPersistConfig, cartSlice.reducer),
  filterProduct: filterProductSlice.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "filterProduct/setFilter",
          "cart/setSelectedItem",
          "cart/setItems",
          "cart/updateItem",
          "cart/setItemQuantity",
        ],
        ignoredActionPaths: [
          "filterProduct.products",
          "cart.selectedItem",
          "cart.items",
        ],
        ignoredPaths: [
          "filterProduct.products",
          "cart.selectedItem",
          "cart.items",
        ],
      },
    }),
});

export default store;
export const persistor = persistStore(store);
